import { AnalyticsComponentProvider, Button, Card, Flex, PageGridCell, PageGridRow, Tag, Text } from "@wayflyer/flyui";
import { RhfDropdownField } from "@wayflyer/flyui-hook-form";
import { useIntl } from "react-intl";
import { usePostHog } from "posthog-js/react";

import { useBankControllerContext } from "../../pages/banking/hooks/bank-controller-context";

import { AuOpenBankingBanner } from "./au-open-banking-banner";
import { BankConnectionsSlideout } from "./bank-connections-slideout";
import { BankStatementsSlideout } from "./bank-statements-slideout";
import { MESSAGES } from "./message-definitions";
import styles from "./bank-controller-next.module.css";

export const BankControllerNext = () => {
  const intl = useIntl();
  const posthog = usePostHog();
  const {
    ready,
    bankConnectProps,
    requireConnection,
    hasBankingConnections,
    bankingConnections,
    countryForm,
    countryOptions,
    defaultCountry,
    isConnectionUnsupportedCountry,
    isConnecting,
    providerName,
    isConnectionsSlideoutOpen,
    isStatementsSlideoutOpen,
    openConnectionsSlideout,
    openStatementsSlideout,
    handleConnect,
    statementCount,
  } = useBankControllerContext();

  const totalAccountCount = bankingConnections.reduce(
    (acc, conn) => acc + conn.accounts.filter((a) => a.pull_enabled).length,
    0
  );

  const selectedCountryCode = countryForm.watch("country");

  const pageLayout = {
    connectionCard: { md: { span: 6 }, sm: { span: 5 }, xxs: { span: 6 } },
    uploadCard: { md: { span: 4 }, sm: { span: 3 }, xxs: { span: 6 } },
  };

  return (
    <AnalyticsComponentProvider component="bank-connect">
      <BankStatementsSlideout isOpen={isStatementsSlideoutOpen} />
      <BankConnectionsSlideout isOpen={isConnectionsSlideoutOpen} />

      <PageGridRow stretch>
        <PageGridCell layout={pageLayout.connectionCard}>
          <Card
            border="gradient"
            slots={{
              header: (
                <Card.Header
                  title={intl.formatMessage(MESSAGES.connectionCardTitle)}
                  subtitle={intl.formatMessage(MESSAGES.connectionCardSubtitle)}
                  adornment={
                    <Tag
                      content={intl.formatMessage(
                        hasBankingConnections
                          ? MESSAGES.bankAccountsConnected
                          : requireConnection
                          ? MESSAGES.requiredBadge
                          : MESSAGES.recommendedBadge,
                        hasBankingConnections ? { count: totalAccountCount } : undefined
                      )}
                      color={hasBankingConnections ? "positive" : "info"}
                      variant="default"
                    />
                  }
                />
              ),
            }}
          >
            {hasBankingConnections ? (
              <Flex height="100%" align="flex-end">
                <Button variant="Secondary" fullWidth onClick={openConnectionsSlideout}>
                  {intl.formatMessage(MESSAGES.manageAccounts)}
                </Button>
              </Flex>
            ) : (
              <Flex direction="column" gap="4">
                <RhfDropdownField
                  name="country"
                  initialValue={defaultCountry?.code}
                  options={countryOptions}
                  form={countryForm}
                  label={intl.formatMessage(MESSAGES.bankLocationLabel)}
                />
                {bankConnectProps.basiqConnectProps.openBankingEnabled && selectedCountryCode === "AU" && (
                  <AuOpenBankingBanner />
                )}
                <Button
                  variant="Secondary"
                  fullWidth
                  onClick={handleConnect}
                  disabled={isConnectionUnsupportedCountry}
                  loading={!ready || isConnecting}
                >
                  {isConnectionUnsupportedCountry
                    ? intl.formatMessage(MESSAGES.connectButtonGeneric)
                    : intl.formatMessage(MESSAGES.connectButton, { providerName })}
                </Button>
              </Flex>
            )}
          </Card>
        </PageGridCell>

        <PageGridCell layout={pageLayout.uploadCard}>
          <Flex direction="column" gap="6" width="100%" height="100%">
            <Flex
              direction="column"
              grow={1}
              minHeight="0"
              width="100%"
              height="100%"
              className={styles.uploadCardFill}
            >
              <Card
                slots={{
                  header: (
                    <Card.Header
                      title={intl.formatMessage(MESSAGES.uploadCardTitle)}
                      subtitle={intl.formatMessage(MESSAGES.uploadCardSubtitle)}
                      adornment={
                        statementCount > 0 ? (
                          <Tag
                            content={intl.formatMessage(MESSAGES.statementsUploadedCount, {
                              count: statementCount,
                            })}
                            color="neutral"
                          />
                        ) : undefined
                      }
                    />
                  ),
                }}
              >
                <Flex height="100%" align="flex-end" width="100%">
                  <Button
                    variant="Tertiary"
                    fullWidth
                    onClick={() => {
                      posthog?.capture("onboarding_bank_upload_selected", { upload_type: "bank_statement" });
                      openStatementsSlideout();
                    }}
                    disabled={requireConnection && !hasBankingConnections}
                  >
                    {statementCount > 0
                      ? intl.formatMessage(MESSAGES.manageStatementsButton)
                      : intl.formatMessage(MESSAGES.uploadAreaLabel)}
                  </Button>
                </Flex>
              </Card>
            </Flex>
            {requireConnection && !hasBankingConnections && (
              <Flex width="100%" justify="center" shrink={0}>
                <Text size="sm" color="placeholder">
                  {intl.formatMessage(MESSAGES.uploadAvailableAfterBankConnection)}
                </Text>
              </Flex>
            )}
          </Flex>
        </PageGridCell>
      </PageGridRow>
    </AnalyticsComponentProvider>
  );
};
